import { Link } from "@tanstack/react-router";
import { CarFront, Route as RouteIcon } from "lucide-react";
import { usePurchases } from "@/api/queries";
import type { PurchaseFilters, PurchaseRow } from "@/api/types";
import { StatusBadge } from "@/components/feedback/status-badge";
import { OrgLink, StoreLink, UserLink } from "@/components/navigation/entity-links";
import { AdminTable, type Column } from "@/components/tables/admin-table";
import { type ResourceTableProps, tablePaging } from "@/components/tables/types";
import { formatShortDateTime } from "@/lib/dates";
import { formatMoney } from "@/lib/formatters";
import { PURCHASE, SOURCE_LABEL, statusOf } from "@/lib/status";

export function PurchasesTable({ query, onChange, toolbar, hide = [], onRowClick, filtered }: ResourceTableProps<PurchaseFilters>) {
  const q = usePurchases(query);
  const columns: Column<PurchaseRow>[] = [
    {
      id: "vehicle",
      header: "Vehicle",
      alwaysVisible: true,
      cell: (p) => (
        <span className="flex flex-col">
          <Link to="/opportunities/$opportunityId" params={{ opportunityId: p.opportunityId }} className="font-medium hover:underline">
            {p.vehicle?.title ?? "Vehicle"}
          </Link>
          <span className="font-mono text-[11px] text-muted-foreground">{p.vehicle?.vin ?? "VIN unknown"}</span>
        </span>
      ),
    },
    { id: "organization", header: "Organization", cell: (p) => <OrgLink org={p.organization} /> },
    { id: "store", header: "Store", cell: (p) => <StoreLink store={p.store} /> },
    { id: "seller", header: "Seller", cell: (p) => p.seller?.name ?? "—" },
    { id: "source", header: "Source", cell: (p) => SOURCE_LABEL[p.source] ?? p.source },
    { id: "price", header: "Purchase price", sortKey: "amountCents", align: "right", cell: (p) => formatMoney(p.amountCents) },
    { id: "buyer", header: "Buyer", cell: (p) => <UserLink user={p.buyer} /> },
    { id: "date", header: "Purchase date", sortKey: "purchasedAt", cell: (p) => <span className="tabular">{formatShortDateTime(p.purchasedAt)}</span> },
    {
      id: "status",
      header: "Status",
      cell: (p) => (p.status ? <StatusBadge config={statusOf(PURCHASE, p.status)} /> : <span className="text-muted-foreground">Not synced</span>),
    },
  ];
  return (
    <AdminTable
      id="purchases"
      label="Purchases"
      resourceName="purchases"
      columns={columns.filter((c) => !hide.includes(c.id))}
      rows={q.data?.items}
      rowKey={(p) => p.id}
      isPending={q.isPending}
      isFetching={q.isFetching}
      error={q.error}
      onRetry={() => void q.refetch()}
      {...tablePaging(query, q.data?.total, onChange)}
      onRowClick={onRowClick ? (p) => onRowClick(p.id) : undefined}
      rowActions={onRowClick ? [{ label: "Trace lead → purchase", icon: RouteIcon, onSelect: (p) => onRowClick(p.id) }] : undefined}
      toolbar={toolbar}
      empty={
        filtered
          ? { icon: CarFront, title: "No purchases match", description: "Adjust the date range or filters." }
          : { icon: CarFront, title: "No purchases recorded", description: "Purchases appear when a buyer records an acquisition." }
      }
    />
  );
}
